export const CHANNEL_ROUTES = {
  digital: 'marketing-digital',
  social: 'marketing-social',
  email: 'marketing-email',
  traffic: 'marketing-traffic',
  print: 'marketing-print'
};

export const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember'
];

// "all" = kein Datumsfilter: Das Backend liefert dann den gesamten verfügbaren Zeitraum.
// Als eigener Wert gespeichert, damit der Standard ("aktuelles Jahr") ihn nicht überschreibt.
export const PERIOD_LABELS = {
  all: 'Gesamter Zeitraum',
  last_7_days: 'Letzte 7 Tage',
  last_30_days: 'Letzte 30 Tage',
  current_month: 'Aktueller Monat',
  current_quarter: 'Aktuelles Quartal'
};

export function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '–';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat('de-DE').format(numeric);
}

export function formatPercent(value) {
  if (value === null || value === undefined) return '–';
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)} %`;
}

// UX: CTR wurde ohne Nachkommastellen gerundet ("0 %" bei 216 Klicks). Kleine Raten
// erhalten 2 Nachkommastellen. Liegen Zähler und Nenner vor, wird nur für die
// Anzeige genauer gerechnet – der API-Wert bleibt die Quelle.
export function formatRate(value, numerator, denominator, digits = 2) {
  let rate = value;
  if (Number.isFinite(Number(numerator)) && Number(denominator) > 0) {
    rate = (Number(numerator) / Number(denominator)) * 100;
  }
  if (rate === null || rate === undefined || rate === '') return '–';
  return `${new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(rate)} %`;
}

export function formatCurrency(value) {
  if (value === null || value === undefined) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}

export function formatKpi(kpi) {
  if (!kpi) return '–';
  if (kpi.format === 'percent') {
    // UX: Raten unter 10 % mit 2 Nachkommastellen, sonst 1 (z. B. 0,05 % statt 0 %).
    const precise = Number(kpi.denominator) > 0 ? (Number(kpi.numerator) / Number(kpi.denominator)) * 100 : kpi.value;
    return formatRate(kpi.value, kpi.numerator, kpi.denominator, precise !== null && precise < 10 ? 2 : 1);
  }
  if (kpi.format === 'currency') return formatCurrency(kpi.value);
  return formatNumber(kpi.value);
}

export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  const date = parseDate(value);
  if (!date) return value ? String(value) : '–';
  return date.toLocaleDateString('de-DE');
}

export function formatDateShort(value) {
  const date = parseDate(value);
  if (!date) return '';
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric' });
}

export function formatMonthLabel(value) {
  const date = parseDate(value);
  if (!date) return '';
  return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

export function isoDate(value) {
  const date = parseDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDays(value, amount) {
  const date = parseDate(value);
  if (!date) return null;
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
  return next;
}

export function startOfWeek(value) {
  const date = parseDate(value);
  if (!date) return null;
  const day = date.getDay();
  const offset = day === 0 ? 6 : day - 1;
  return addDays(date, -offset);
}

export function startOfMonth(value) {
  const date = parseDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(value) {
  const date = parseDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function isoWeekNumber(value) {
  const date = parseDate(value);
  if (!date) return null;
  const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7));
  const week1 = new Date(thursday.getFullYear(), 0, 4);
  return 1 + Math.round((thursday - week1) / 604800000);
}

export function formatPeriodLabel(filters) {
  if (!filters) return 'Alle Zeiträume';
  if (filters.date_from || filters.date_to) {
    if (filters.date_from && filters.date_to) {
      return `${formatDate(filters.date_from)} – ${formatDate(filters.date_to)}`;
    }
    if (filters.date_from) return `ab ${formatDate(filters.date_from)}`;
    return `bis ${formatDate(filters.date_to)}`;
  }
  if (filters.period && PERIOD_LABELS[filters.period]) return PERIOD_LABELS[filters.period];
  if (filters.year && filters.month) {
    const monthName = MONTH_NAMES[Number(filters.month) - 1];
    return monthName ? `${monthName} ${filters.year}` : String(filters.year);
  }
  if (filters.year && filters.quarter) return `Q${filters.quarter} ${filters.year}`;
  if (filters.year) return `Jahr ${filters.year}`;
  return 'Alle Zeiträume';
}
